import React, { useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, IconButton, Popover, Stack, TextField } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import classNames from "classnames";

import "./InfoCard.styles.scss";
import { CubicData, CubicPageType, ReportsQueries, CubicPayload } from "store/types";
import { useReportsActions, useReportsMetaSelector, useReportsQueriesSelector } from "store";
import { SelectAutocomplete } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DEAL_INDEX } from "constants/reports";
import { indexNames } from "constants/reports";
import { useFormatNumericHook } from "hooks";

interface InfoCardProps extends CubicData {
  isLoading: boolean;
  name: string;
  pageType: CubicPageType;
  configurable?: boolean;
  borderColor?: string;
}

export const InfoCard: React.FC<InfoCardProps> = ({
  value,
  title,
  isLoading,
  configurable = true,
  tooltip,
  name,
  filter,
  pageType,
  indexName,
  borderColor = "#bae8e5",
  pme_warning,
  apply_number_format
}) => {
  const { KPIFields, KPICharacteristics } = useReportsMetaSelector();
  const { setQueriesAction } = useReportsActions();
  const queries = useReportsQueriesSelector();
  const formatNumeric = useFormatNumericHook();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const kpiOptionsForSelect: ReportsQueries["calculatedFields"] = useMemo(() => {
    return [...KPIFields, ...(pageType === "cubicDeal" ? KPICharacteristics : [])];
  }, [KPIFields, KPICharacteristics, pageType]);

  const getInitialCubicState = () => ({
    title: title,
    filter,
    ...(filter === "PME" && { indexName: indexName || { index: DEAL_INDEX, name: "Use deal specific index" } })
  });

  const [cubic, setCubic] = useState<CubicPayload>(getInitialCubicState());

  useEffect(() => {
    setCubic(getInitialCubicState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, filter, indexName]);

  const handleApply = () => {
    setAnchorEl(null);
    if (pageType === "cubicSnapshot" || pageType === "cubicDeal") {
      const queryData = {
        title: cubic.title,
        filter: cubic.filter,
        ...(cubic.filter === "PME" && cubic.indexName?.index !== DEAL_INDEX && { indexName: cubic.indexName })
      };

      setQueriesAction({
        [pageType]: {
          ...queries[pageType],
          [name]: queryData
        }
      });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCubic(getInitialCubicState());
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setCubic(getInitialCubicState());
  };

  return (
    <div className={classNames("info-card", { "info-card--loading": isLoading })} style={{ borderColor }}>
      {Boolean(pme_warning) && !isLoading && (
        <MaterialTooltip isShow title={pme_warning} centerText placement="top">
          <InfoOutlinedIcon fontSize="sm" sx={{ fill: "#071F35", position: "absolute", top: "40px", left: "10px" }} />
        </MaterialTooltip>
      )}

      {!isLoading && configurable && (
        <IconButton
          onClick={handleOpen}
          className="settings-button"
          sx={{
            position: "absolute",
            right: "6px",
            top: "8px",
            zIndex: 100,
            transition: "opacity 0.15s linear",
            opacity: anchorEl ? 1 : 0
          }}
        >
          <TuneIcon sx={{ height: 20, width: 20, color: "#5d7782" }} />
        </IconButton>
      )}
      {anchorEl && (
        <Popover
          open={!!anchorEl}
          onClose={handleClose}
          anchorEl={anchorEl}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "8px",
                boxShadow: "sm"
              }
            }
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Stack sx={{ p: "10px", gap: "10px", borderRadius: "8px", boxShadow: "sm" }}>
            <SelectAutocomplete<string>
              options={kpiOptionsForSelect}
              value={kpiOptionsForSelect.includes(cubic.filter) ? cubic.filter : kpiOptionsForSelect[0]}
              onChange={(value) => {
                if (!value) return;
                setCubic({
                  filter: (value as string) || "",
                  ...(value !== filter ? { title: value } : { title }),
                  ...(value === "PME" && { indexName: { index: DEAL_INDEX, name: "Use deal specific index" } })
                });
              }}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Value" size="small" />}
              limitTags={2}
            />
            {cubic.filter === "PME" && (
              <SelectAutocomplete<string>
                options={["Use deal specific index", ...Object.values(indexNames)]}
                value={cubic.indexName?.name ?? ""}
                disableClearable
                onChange={(selectedValue) => {
                  if (!selectedValue) return;
                  if (selectedValue === "Use deal specific index") {
                    setCubic({ ...cubic, indexName: { index: DEAL_INDEX, name: selectedValue } });
                    return;
                  }
                  const indexKey = Object.entries(indexNames).find(([, val]) => val === selectedValue)?.[0];
                  if (indexKey) {
                    setCubic({ ...cubic, indexName: { index: indexKey, name: selectedValue } });
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Index" size="small" />}
                limitTags={2}
              />
            )}
            <TextField
              value={cubic.title}
              label="Rename"
              variant="outlined"
              onChange={(event) => {
                setCubic({
                  ...cubic,
                  title: event.target.value
                });
              }}
            />
            <Button variant="contained" sx={{ alignSelf: "flex-end" }} onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Popover>
      )}
      {isLoading ? (
        <CircularProgress size={80} />
      ) : (
        <div className="info-card__content">
          <span className="info-card__title">{title}</span>
          <MaterialTooltip isShow variant="dark" title={tooltip || value} placement="bottom">
            <span className="info-card__data">{apply_number_format ? formatNumeric({ value: tooltip || value, format: {} }) : (tooltip || value)}</span>
          </MaterialTooltip>
        </div>
      )}
    </div>
  );
};
